import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, combineLatest, BehaviorSubject, of, interval, Subscription, from } from 'rxjs';
import { map, switchMap, take, shareReplay, concatMap, catchError, toArray } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { 
  faCopy, 
  faCheckCircle, 
  faThumbsUp,
  faExclamationTriangle,
  faPen,
  faCalendar,
  faChevronDown,
  faChevronUp,
  faSync
} from '@fortawesome/free-solid-svg-icons';

import { OrderSearchService } from '../manage-orders/services/order-search.service';
import { OrderSearchModel } from '../manage-orders/models/order-search.models';
import { ParticipantsService } from '../shared/services/participants.service';
import { Participant } from '../shared/models/participant.model';
import { DateHelper } from '../shared/helpers/date.helper';
import { AppSpinnerModule } from '../shared/modules/app-spinner.module';
export interface OrderReportSummary {
  totalCompletedToday: number;
  totalInProgress: number;
  totalScheduledToday: number;
  totalFailedToday: number;
}

export interface InProgressOrderDetail {
  orderId: number;
  loanIdentifier: string;
  borrowerNames: string[];
  clientName: string;
  scheduledDateTime: string;
  statusCode: string;
  statusUpdatedDate: string;
  orderType: string;
  thirdPartyOrderId: string;
  participants: Participant[];
  signingAgentEmails: string[];
}

@UntilDestroy()
@Component({
  selector: 'app-todays-orders-report',
  templateUrl: './todays-orders-report.component.html',
  styleUrls: ['./todays-orders-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TodaysOrdersReportComponent implements OnInit {
  faSync = faSync;
  faCopy = faCopy;
  faCheckCircle = faCheckCircle;
  faThumbsUp = faThumbsUp;
  faExclamationTriangle = faExclamationTriangle;
  faPen = faPen;
  faCalendar = faCalendar;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  
  expandedOrders = new Set<number>();
  autoRefreshEnabled = false;
  isCopyingEmails = false;
  
  private refreshSubject = new BehaviorSubject<void>(undefined);
  private autoRefreshSubscription?: Subscription;
  
  orderSummary$: Observable<OrderReportSummary>;
  inProgressOrders$: Observable<InProgressOrderDetail[]>;
  private allOrdersToday$: Observable<{ totalRows: number; results: any[] }>;
  
  constructor(
    private orderSearchService: OrderSearchService,
    private participantsService: ParticipantsService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private dateHelper: DateHelper,
    private spinner: AppSpinnerModule
  ) {}

  ngOnInit(): void {
    this.initializeData();
  }

  toggleOrderExpansion(orderId: number): void {
    if (this.expandedOrders.has(orderId)) {
      this.expandedOrders.delete(orderId);
    } else {
      this.expandedOrders.add(orderId);
    }
  }

  isOrderExpanded(orderId: number): boolean {
    return this.expandedOrders.has(orderId);
  }

  private initializeData(): void {
    this.spinner.show();
    const refreshTrigger$ = this.refreshSubject.asObservable();
    
    this.allOrdersToday$ = refreshTrigger$.pipe(
      switchMap(() => this.orderSearchService.search(this.createActiveOrdersSearchParams())),
      shareReplay(1),
      untilDestroyed(this)
    );

    this.orderSummary$ = this.allOrdersToday$.pipe(
      map(results => {
        const orders = results.results;
        
        const completedCount = orders.filter(o => o.order.statusCode === 'COMPLETE').length;
        const readyCount = orders.filter(o => o.order.statusCode === 'READY').length;
        
        const inProgressStatuses = ['INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING'];
        const inProgressCount = orders.filter(o => inProgressStatuses.includes(o.order.statusCode)).length;
        
        const failedStatuses = ['SIGNINGFAILED', 'OPTOUT', 'CANCELLED', 'VAULTINGFAILED'];
        const { yearStart: startOfToday, yearEnd: endOfToday } = this.getDateRange();
        
        const failedCount = orders.filter(o => {
          if (!failedStatuses.includes(o.order.statusCode)) return false;
          
          const formattedDate = this.formatUpdatedDateTime(o.order.updatedDate);
          const updatedDate = new Date(formattedDate);
          return updatedDate >= startOfToday && updatedDate <= endOfToday;
        }).length;

        this.spinner.hide();

        return {
          totalCompletedToday: completedCount,
          totalInProgress: inProgressCount,
          totalScheduledToday: readyCount,
          totalFailedToday: failedCount
        };
      }),
      untilDestroyed(this)
    );

    this.inProgressOrders$ = this.allOrdersToday$.pipe(
      switchMap(results => {
        if (results.results.length === 0) {
          return of([]);
        }
        
        const includedStatuses = ['INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING'];
        const filteredResults = results.results.filter(order => 
          includedStatuses.includes(order.order.statusCode)
        );
        
        if (filteredResults.length === 0) {
          return of([]);
        }
        
        return combineLatest(
          filteredResults.map(order => 
            this.participantsService.getParticipants(order.order.id).pipe(
              map(participants => ({
                orderId: order.order.id,
                loanIdentifier: order.order.loanIdentifier,
                borrowerNames: order.borrowerNames,
                clientName: order.clientName,
                scheduledDateTime: order.order.scheduledDateTime.toString(),
                statusCode: order.order.statusCode,
                statusUpdatedDate: order.order.updatedDate ? order.order.updatedDate.toString() : '',
                orderType: this.getDisplayProductType(order.order.productType),
                thirdPartyOrderId: order.order.thirdPartyOrderIdentifier || 'N/A',
                participants,
                signingAgentEmails: participants
                  .filter(p => p.role === 'SIGNINGAGENT')
                  .map(p => p.email)
                  .filter(email => !!email)
              } as InProgressOrderDetail))
            )
          )
        );
      }),
      shareReplay(1),
      untilDestroyed(this)
    );
  }

  private getEmailsFromOrders(orders: any[]): Observable<string[]> {
    if (orders.length === 0) {
      return of([]);
    }
    
    const batchSize = 50;
    const batches: any[][] = [];
    
    for (let i = 0; i < orders.length; i += batchSize) {
      batches.push(orders.slice(i, i + batchSize));
    }
    
    return from(batches).pipe(
      concatMap(batch => 
        combineLatest(
          batch.map((order: any) => 
            this.participantsService.getParticipants(order.order.id).pipe(
              map(participants => participants
                .filter(p => p.role === 'SIGNINGAGENT')
                .map(p => p.email)
                .filter(email => !!email)
              ),
              catchError(error => {
                console.warn(`Failed to get participants for order ${order.order.id}:`, error);
                return of([]);
              })
            )
          )
        ).pipe(
          map((orderEmails: string[][]) => orderEmails.flat()),
          catchError(error => {
            console.warn('Error processing batch:', error);
            return of([]);
          })
        )
      ),
      toArray(),
      map((batchResults: string[][]) => batchResults.flat())
    );
  }

  private createActiveOrdersSearchParams(): OrderSearchModel {
    const { yearStart, yearEnd } = this.getDateRange();

    return {
      clientId: 0,
      startRow: 1,
      maxRow: 100000,
      scheduledDateTimeStart: yearStart.toISOString(),
      scheduledDateTimeEnd: yearEnd.toISOString(),
      isSortOrderAscending: false,
      sortByColumn: 'ScheduledDateTime',
      showOnly: []
    };
  }

  private getDateRange(): { yearStart: Date; yearEnd: Date } {
    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    return {
      yearStart: startOfDay,
      yearEnd: endOfDay
    };
  }

  private onAutoRefresh(): void {
    this.refreshSubject.next();
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    
    this.autoRefreshSubscription = interval(60000)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.onAutoRefresh();
      });
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
      this.autoRefreshSubscription = undefined;
    }

  }

  onCopySigningAgentEmails(): void {
    this.isCopyingEmails = true;
    
    combineLatest([
      this.inProgressOrders$.pipe(
        map(orders => orders.flatMap(order => order.signingAgentEmails))
      ),
      this.allOrdersToday$.pipe(
        switchMap(results => {
          const readyOrders = results.results.filter((order: any) => order.order.statusCode === 'READY');
          return this.getEmailsFromOrders(readyOrders);
        })
      )
    ]).pipe(
      map(([inProgressEmails, readyEmails]) => {
        const allEmails = [...inProgressEmails, ...readyEmails];
        return allEmails.filter((email, index, array) => array.indexOf(email) === index);
      }),
      take(1)
    ).subscribe({
      next: (signingAgentEmails) => {
        const emailString = signingAgentEmails.join('; ');

        if (emailString) {
          navigator.clipboard.writeText(emailString).then(() => {
            this.isCopyingEmails = false;
            this.cdr.detectChanges();
            this.toastr.success(`Successfully copied signing agent emails to clipboard!`);
          }).catch(() => {
            const textArea = document.createElement('textarea');
            textArea.value = emailString;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            this.isCopyingEmails = false;
            this.cdr.detectChanges();
            this.toastr.success(`Successfully copied signing agent emails to clipboard!`);
          });
        } else {
          this.isCopyingEmails = false;
          this.cdr.detectChanges();
          this.toastr.info('No signing agent emails found in ready or active orders.');
        }
      },
      error: (error) => {
        console.error('Error copying signing agent emails:', error);
        this.isCopyingEmails = false;
        this.cdr.detectChanges();
        this.toastr.error('Failed to copy signing agent emails.');
      }
    });
  }



  getStatusDisplayClass(statusCode: string): string {
    switch (statusCode) {
      case 'READY':
        return 'pill ready';
      case 'INPROGRESS':
      case 'SIGNING':
      case 'SESSIONCOMPLETE':
      case 'VAULTING':
        return 'pill info';
      case 'COMPLETE':
        return 'pill complete';
      case 'CANCELLED':
      case 'OPTOUT':
      case 'SIGNINGFAILED':
      case 'VAULTINGFAILED':
        return 'pill error';
      default:
        return 'pill';
    }
  }

  formatDateTime(dateTime: string): string {
    const convertedDate = this.dateHelper.convertDateTimeToLocalDateTime(dateTime);
    return convertedDate.toLocaleString();
  }

  formatUpdatedDateTime(dateTime: string): string {
    const timezoneMatch = dateTime.match(/([+-])(\d{2}):(\d{2})$/);
    if (timezoneMatch) {
      const sign = timezoneMatch[1] === '-' ? -1 : 1;
      const offsetHours = parseInt(timezoneMatch[2]);
      const offsetMinutes = parseInt(timezoneMatch[3]);

      const date = new Date(dateTime);
      date.setHours(date.getHours() + (sign * offsetHours));
      date.setMinutes(date.getMinutes() + (sign * offsetMinutes));
      date.setSeconds(0);
      date.setMilliseconds(0);

      return date.toLocaleString();
    }

    const date = new Date(dateTime);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date.toLocaleString();
  }

  getStepCompleted(stepStatus: string, currentStatus: string): boolean {
    const statusOrder = ['READY', 'INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING', 'COMPLETE'];
    const stepIndex = statusOrder.indexOf(stepStatus);
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    if (currentIndex === -1) {
      return stepIndex <= 1;
    }
    
    return currentIndex > stepIndex;
  }

  getDisplayProductType(productType: string): string {
    if (!productType) {
      return 'N/A';
    }
    
    switch (productType) {
      case 'RemoteSigning':
        return 'RON';
      case 'IPEN':
        return 'IPEN';
      case 'Hybrid':
        return 'Hybrid';
      case 'KnownSignerRON':
        return 'KRON';
      case 'eSign':
        return 'eSign';
      default:
        return productType;
    }
  }
}
